//@flow
/*
    NOTE: This file is used by import worker don't import any DOM related stuff
    incase DOM things needs to be imported use isWorkerThread util and do dynamic import
*/
import * as Immutable from 'immutable';
import I18nHelper from 'common/helpers/I18nHelper';
import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';
import { EntityImportHelper } from 'common/entity/helpers/EntityImportHelper';
import { PhoneNumber } from 'common/models/internal/PhoneNumber';
import { EmailAddress } from 'common/models/internal/EmailAddress';
import { ContactType, Contact } from 'common/models/internal/Contact';
import DataUtils from 'common/utils/DataUtils';
import type { EntityImportRowModelType } from 'common/models/flow/EntityImportTable';
const nsTranslate = I18nHelper.getTranslate(['entityManagement']);
export class ContactImportHelper extends EntityImportDataHelper {
    static validateEmailAddress({ entity }: Object) {
        const emailAddress = entity.getIn(['emailAddress', 'emailAddress']);
        return super.validateEmailAddress(emailAddress);
    }
    static processAdditionalPhoneNumbers({
        cellValue,
        defaultCountryISOCode
    }: Object): Array<Object> {
        if (!cellValue) {
            return [];
        }
        const phoneNumbers = cellValue.split(',');
        const validPhoneNumbers = phoneNumbers.reduce(
            (additionalPhoneNumbers, phoneNumber) => {
                const sanitisedNumber = phoneNumber.trim();
                if (
                    !DataUtils.isEmpty(sanitisedNumber) &&
                    defaultCountryISOCode !== sanitisedNumber
                ) {
                    return [
                        ...additionalPhoneNumbers,
                        { phoneNumber: sanitisedNumber }
                    ];
                }
                return additionalPhoneNumbers;
            },
            []
        );
        return validPhoneNumbers;
    }

    static serializeAdditionalPhoneNumbers(
        phoneNumbers: Array<Object>
    ): Immutable.List<PhoneNumber> {
        return Immutable.List(phoneNumbers).map((item: PhoneNumber) =>
            PhoneNumber.fromJS(item)
        );
    }

    static validateAdditionalPhoneNumbers({ entity }: Object) {
        const additionalPhoneNumbers = entity.get('additionalPhoneNumbers');
        if (additionalPhoneNumbers.size > 10) {
            return nsTranslate(
                'contactValidations.alternateNumbersMoreThanLimit'
            );
        }
        return entity.validateAdditionalPhoneNumbers();
    }

    static processAdditionalEmails({ cellValue }: Object) {
        return cellValue
            ? [
                  {
                      emailAddress: cellValue.trim()
                  }
              ]
            : [];
    }

    static serializeAdditionalEmails(
        emails: Array<Object>
    ): Immutable.List<EmailAddress> {
        return Immutable.List(emails).map((item: EmailAddress) =>
            EmailAddress.fromJS(item)
        );
    }

    static validateAdditionalEmails({ entity }: Object) {
        const emailAddress = entity.getIn([
            'additionalEmailAddresses',
            0,
            'emailAddress'
        ]);
        return super.validateEmailAddress(emailAddress);
    }

    static processContactType = ({ cellValue }: { cellValue: any }) => {
        if (cellValue) {
            const translatedContactTypes = ContactType.getOptions({
                translationKey: 'contactProperties.contactType'
            });
            const matchedContact = translatedContactTypes.find(
                ({ label }) => label === cellValue
            );
            if (matchedContact) {
                return matchedContact.value;
            }
            return ' '; // HACK for import when user value from contact enum
        }
        return cellValue;
    };

    static processEntityRows = ({
        rows
    }: {
        rows: Immutable.List<EntityImportRowModelType>
    }): Immutable.List<Contact> => {
        return rows.map(entityRow =>
            entityRow.map((cellValue, headerId) => {
                if (headerId === 'customProperties') {
                    return cellValue.filter(value => value);
                }
                return cellValue;
            })
        );
    };

    static generateEntityList = (
        contactsData: Array<*>
    ): Immutable.List<Contact> =>
        Immutable.List(
            contactsData.map(contactEntity => Contact.fromJS(contactEntity))
        );

    static getDataForCell = ({ localeSettings }) => {
        return {
            defaultCountryISOCode: localeSettings.countryDialCode
        };
    };

    static getUploadedEntities = ({
        uploadedTable,
        clientId,
        getDataForCell
    }: {
        uploadedTable: Object,
        clientId: string,
        getDataForCell: () => Object
    }): Immutable.List<Contact> => {
        const contactsData = EntityImportHelper.makeDataForEntities({
            table: uploadedTable,
            clientId,
            getDataForCell,
            processEntityRows: ContactImportHelper.processEntityRows
        });

        const contactEntities = Immutable.List(
            contactsData.map(contactEntity =>
                Contact.fromJS(contactEntity.toJS())
            )
        );

        return contactEntities;
    };

    static getCustomFields(entitySettings) {
        return entitySettings?.contactSettings?.customFields;
    }
}

export default ContactImportHelper;
